import { useState } from "react";
import { getVerticalMeta } from "@/components/verticals/VerticalIcons";
import { VerticalPickerDialog, VerticalBadge } from "@/components/verticals/VerticalPickerDialog";
import { 
  FileText,
  Plus,
  MoreVertical,
  Edit,
  Loader2,
  CheckCircle2,
  XCircle,
  Search,
  Trash2,
  Wand2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTemplates, useCreateTemplate, useUpdateTemplate } from "@/hooks/use-queries";
import { Skeleton } from "@moltjobs/ui";
import { toast } from "sonner";
import { JobTemplate } from "@/lib/api";

const isValidJson = (str: string): boolean => {
  if (!str.trim()) return true;
  try { JSON.parse(str); return true; } catch { return false; }
};

const tryFormat = (str: string): string => {
  try { return JSON.stringify(JSON.parse(str), null, 2); } catch { return str; }
};

export default function Templates() {
  const [search, setSearch] = useState("");
  const { data: response, isLoading } = useTemplates({ search });
  const templates = response?.data;

  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<JobTemplate | null>(null);

  // Form State
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    vertical: "CUSTOM",
    estimatedDurationMs: 0,
    inputSchema: "{}",
    outputSchema: "{}",
    isActive: true,
  });

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      id: "",
      name: "",
      vertical: "CUSTOM",
      estimatedDurationMs: 3600000, // 1 hour default
      inputSchema: JSON.stringify({
        type: "object",
        properties: {},
        required: []
      }, null, 2),
      outputSchema: JSON.stringify({
        type: "object",
        properties: {},
        required: []
      }, null, 2),
      isActive: true,
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (template: JobTemplate) => {
    setEditingTemplate(template);
    setFormData({
      id: template.id,
      name: template.name,
      vertical: template.vertical,
      estimatedDurationMs: template.estimatedDurationMs || 0,
      inputSchema: JSON.stringify(template.inputSchema, null, 2),
      outputSchema: JSON.stringify(template.outputSchema, null, 2),
      isActive: template.isActive,
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      // Validate JSON
      let inputSchemaObj, outputSchemaObj;
      try {
        inputSchemaObj = JSON.parse(formData.inputSchema);
        outputSchemaObj = JSON.parse(formData.outputSchema);
      } catch (e) {
        toast.error("Invalid JSON in Schema fields");
        return;
      }

      if (!formData.id || !formData.name) {
        toast.error("ID and Name are required");
        return;
      }

      const payload = {
        name: formData.name,
        vertical: formData.vertical,
        estimatedDurationMs: Number(formData.estimatedDurationMs),
        inputSchema: inputSchemaObj,
        outputSchema: outputSchemaObj,
        isActive: formData.isActive,
      };

      if (editingTemplate) {
        await updateMutation.mutateAsync({ id: editingTemplate.id, data: payload });
        toast.success("Template updated successfully");
      } else {
        await createMutation.mutateAsync({ ...payload, id: formData.id });
        toast.success("Template created successfully");
      }
      setIsDialogOpen(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to save template");
    }
  };

  const handleToggleActive = async (template: JobTemplate) => {
    try {
      await updateMutation.mutateAsync({ 
        id: template.id, 
        data: { isActive: !template.isActive } 
      });
      toast.success(`Template ${template.isActive ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const jsonErrors = {
    inputSchema: !isValidJson(formData.inputSchema),
    outputSchema: !isValidJson(formData.outputSchema),
  };
  const hasJsonError = jsonErrors.inputSchema || jsonErrors.outputSchema;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold">Job Templates</h1>
          <p className="text-muted-foreground">Manage standardized job formats.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-full md:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button className="shrink-0" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" /> Create Template
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          [1, 2, 3].map(i => <Skeleton key={i} className="h-48 w-full" />)
        ) : templates?.length === 0 ? (
          <div className="col-span-full py-20 text-center border-2 border-dashed border-divider rounded-xl bg-surface-subtle">
             <FileText className="mx-auto h-12 w-12 text-muted-foreground opacity-20 mb-4" />
             <p className="text-muted-foreground">No templates found.</p>
          </div>
        ) : (
          templates?.map((template) => (
            <Card key={template.id} className={`glass-panel hover:border-primary/50 transition-all group ${!template.isActive && "opacity-60"}`}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                <div className="molt-icon-surface h-10 w-10 rounded-lg flex items-center justify-center text-primary">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="flex items-center gap-2">
                  {template.isActive ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="-mr-2 -mt-2">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(template)}>
                        <Edit className="mr-2 h-4 w-4" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleActive(template)}>
                        {template.isActive ? (
                          <>
                            <XCircle className="mr-2 h-4 w-4 text-red-500" /> Deactivate
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" /> Activate
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div>
                  <CardTitle className="text-lg">{template.name}</CardTitle>
                  {(() => { const { label, Icon, color } = getVerticalMeta(template.vertical); return <CardDescription className={`mt-1 text-xs font-semibold flex items-center gap-1 ${color}`}><Icon size={10} />{label}</CardDescription>; })()}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <Badge variant="outline" className="font-mono text-[10px] opacity-70">ID: {template.id}</Badge>
                  <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => handleEdit(template)}>
                    <Edit className="mr-2 h-3 w-3" /> Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? "Edit Template" : "Create New Template"}</DialogTitle>
            <DialogDescription>
              Define the schema and properties for this job type.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="id">Template ID</Label>
                <Input 
                  id="id" 
                  value={formData.id} 
                  onChange={(e) => setFormData({...formData, id: e.target.value})}
                  disabled={!!editingTemplate}
                  placeholder="e.g. twitter-scraper-v1"
                />
              </div>
              <div className="space-y-2">
                <Label>Vertical</Label>
                <VerticalPickerDialog value={formData.vertical} onChange={(v) => setFormData({...formData, vertical: v})}>
                  <button type="button" className="w-full text-left">
                    <VerticalBadge value={formData.vertical} placeholder="Choose vertical…" />
                  </button>
                </VerticalPickerDialog>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Display Name</Label>
              <Input 
                id="name" 
                value={formData.name} 
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g. Twitter Follower Scraper"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">Est. Duration (ms)</Label>
              <Input 
                id="duration" 
                type="number"
                value={formData.estimatedDurationMs} 
                onChange={(e) => setFormData({...formData, estimatedDurationMs: Number(e.target.value)})}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="inputSchema">Input Schema (JSON)</Label>
                <button type="button" onClick={() => setFormData(f => ({ ...f, inputSchema: tryFormat(f.inputSchema) }))} disabled={jsonErrors.inputSchema} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed">
                  <Wand2 className="h-3 w-3" />Format
                </button>
              </div>
              <Textarea
                id="inputSchema"
                className={`font-mono text-xs min-h-[150px] ${jsonErrors.inputSchema ? "border-destructive focus-visible:ring-destructive" : ""}`}
                value={formData.inputSchema}
                onChange={(e) => setFormData({...formData, inputSchema: e.target.value})}
              />
              {jsonErrors.inputSchema && <p className="text-xs text-destructive">Invalid JSON</p>}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="outputSchema">Output Schema (JSON)</Label>
                <button type="button" onClick={() => setFormData(f => ({ ...f, outputSchema: tryFormat(f.outputSchema) }))} disabled={jsonErrors.outputSchema} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed">
                  <Wand2 className="h-3 w-3" />Format
                </button>
              </div>
              <Textarea
                id="outputSchema"
                className={`font-mono text-xs min-h-[150px] ${jsonErrors.outputSchema ? "border-destructive focus-visible:ring-destructive" : ""}`}
                value={formData.outputSchema}
                onChange={(e) => setFormData({...formData, outputSchema: e.target.value})}
              />
              {jsonErrors.outputSchema && <p className="text-xs text-destructive">Invalid JSON</p>}
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <Switch 
                id="isActive" 
                checked={formData.isActive}
                onCheckedChange={(c) => setFormData({...formData, isActive: c})}
              />
              <Label htmlFor="isActive">Active (Visible to users)</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending || hasJsonError}>
              {createMutation.isPending || updateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
              ) : null}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
